// Lazily-created, process-wide AudioContext shared by every VoiceOrb analyser so opening
// several orbs (or reopening one) never hits the browser's "too many AudioContexts" limit.
// Never closed — that's the normal pattern for this kind of always-on visualizer.
let sharedContext = null;

export function getAudioContext() {
  if (sharedContext) return sharedContext;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  sharedContext = new Ctor();
  return sharedContext;
}

/** Web Audio requires a user gesture to leave "suspended" — call this from a click handler. */
export function resumeAudioContext() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    void ctx.resume().catch(() => undefined);
  }
  return ctx;
}

// MediaElementAudioSourceNode can only be created ONCE per <audio>/<video> element — a second
// call throws InvalidStateError. Cache nodes per element (and per stream, for symmetry) so
// remounting a component that re-runs the same source through the analyser never breaks it.
const sourceNodeCache = new WeakMap();

/**
 * Returns a Web Audio source node for `source` (a MediaStream or HTMLMediaElement), creating
 * and caching it on first use. Returns `{node, isElement}` or null if unsupported/unavailable.
 */
export function getOrCreateSourceNode(audioCtx, source) {
  if (!audioCtx || !source) return null;

  const cached = sourceNodeCache.get(source);
  if (cached) return cached;

  try {
    const isElement = typeof HTMLMediaElement !== "undefined" && source instanceof HTMLMediaElement;
    const node = isElement ? audioCtx.createMediaElementSource(source) : audioCtx.createMediaStreamSource(source);
    const entry = {node, isElement};
    sourceNodeCache.set(source, entry);
    return entry;
  } catch {
    return null;
  }
}
